import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseUrls } from '@app/app.constants';
import { EndUserWithId } from '@app/core/interfaces/c5/end-user';
import { Site } from '@app/core/interfaces/site';
import { SiteCommon } from '@app/core/interfaces/site-common';
import { SiteLocal } from '@app/core/interfaces/site-local';
import { SiteWithLocations } from '@app/core/interfaces/site-with-locations';
import { UserLevel } from '@app/core/models/user-level';
import { EnhancedEndUser } from '@app/shared/models/enhanced-end-user';
import { Utils } from '@app/shared/services/utils.service';
import { wrapError } from '@app/utils/rxjs';
import { enhanceUsersWithExtension } from '@app/utils/user';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { Announcement } from '../interfaces/c5/announcement';

@Injectable({
  providedIn: 'root',
})
export class SiteRepository {
  constructor(
    private http: HttpClient,
    private utils: Utils
  ) {}

  getSiteCommon(): Observable<SiteCommon> {
    return this.utils.apiUrl('/siteCommon').pipe(
      mergeMap((url) => this.http.get<SiteCommon>(url)),
      wrapError('site-repository:get-site-common')
    );
  }

  getSiteWithLocations(siteId: number | string): Observable<SiteWithLocations> {
    return this.utils.apiUrl(`/locations/${siteId}`).pipe(
      mergeMap((url) => this.http.get<SiteWithLocations>(url)),
      wrapError('site-repository:fetch-site-with-locations')
    );
  }

  listAnnouncementsForSite(siteId: number | string): Observable<Announcement[]> {
    return this.utils.apiUrl(`/site/${siteId}/announcements`).pipe(
      mergeMap((url) => this.http.get<Announcement[]>(url)),
      wrapError('site-repository:fetch-announcements-for-site')
    );
  }

  listEndUsers(siteId: number | string): Observable<EnhancedEndUser[]> {
    return this.utils.apiUrl(`/sites/${siteId}/endUsers`).pipe(
      mergeMap((url) => this.http.get<EndUserWithId[]>(url)),
      enhanceUsersWithExtension(),
      wrapError('site-repository:list-end-users')
    );
  }

  listSites(): Observable<Site[]> {
    return this.utils.commonApiUrl('/sites').pipe(
      mergeMap((url) => this.http.get<Site[]>(url)),
      wrapError('site-repository:list-sites')
    );
  }

  listSitesLocal(): Observable<SiteLocal[]> {
    return this.utils.apiUrl('/sitesLocal').pipe(
      mergeMap((url) => this.http.get<SiteLocal[]>(url)),
      wrapError('site-repository:list-sites-local')
    );
  }

  listSitesWithLocations(): Observable<SiteWithLocations[]> {
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + '/locations',
        [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + '/locations',
        [UserLevel.EndUser]: BaseUrls.Enterprise.Enterprise + '/locations',
      })
      .pipe(
        mergeMap((url) => this.http.get<SiteWithLocations[]>(url)),
        wrapError('site-repository:list-sites-with-locations')
      );
  }
}
